import { CONCEPT_REGISTRY, type ConceptId } from '@fcbh/search';
import type { SampleChapter, SampleVerse } from '../data/sample-chapter.js';

// Generic chapter shape — works with SampleChapter and ReaderChapter alike.
export type ChapterLike = {
  bookName: string;
  chapter: number;
  verses: { ref: string; number: number; text: string }[];
};

const STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'of', 'in', 'on', 'to', 'for', 'with', 'by',
  'is', 'are', 'was', 'were', 'be', 'been', 'as', 'at', 'that', 'this', 'it',
  'his', 'her', 'him', 'she', 'he', 'they', 'them', 'their', 'unto', 'from',
  'thou', 'thee', 'thy', 'shall', 'said', 'saying', 'came', 'went', 'hath',
  'which', 'also', 'have', 'had', 'will', 'not', 'but', 'all', 'then', 'so',
]);

function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s']/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((t) => !STOPWORDS.has(t) && t.length > 1);
}

// ─────────────────────────────────────────────────────────────────────────────
// Expanded concept dictionary — covers themes, people, events, and emotions.
// Keys must be valid ConceptId values from @fcbh/search.
// ─────────────────────────────────────────────────────────────────────────────
const CONCEPT_KEYWORDS: Partial<Record<ConceptId, string[]>> = {
  // Core spiritual themes
  love: ['love', 'loved', 'beloved', 'charity', 'loving'],
  forgiveness: ['forgive', 'forgiven', 'forgiveness', 'pardon', 'pardoned', 'remission'],
  hope: ['hope', 'hoping', 'hoped'],
  faith: ['faith', 'believe', 'believed', 'belief', 'trust', 'trusted'],
  fear: ['fear', 'afraid', 'feared', 'terror', 'dread', 'tremble'],
  judgment: ['judge', 'judgment', 'judges', 'condemn', 'condemnation', 'wrath'],
  mercy: ['mercy', 'merciful', 'compassion', 'pity', 'lovingkindness'],
  grace: ['grace', 'gracious', 'favour', 'favor'],
  peace: ['peace', 'peaceful', 'shalom', 'rest', 'reconcile'],
  joy: ['joy', 'rejoice', 'glad', 'gladness', 'delight', 'happiness', 'rejoicing'],
  salvation: [
    'save', 'saved', 'saviour', 'savior', 'salvation', 'redeem', 'redeemed',
    'redemption', 'deliver', 'deliverance', 'rescue',
    'preach', 'preached', 'gospel', 'good news', 'proclaim', 'witness',
    'testimony', 'mission', 'nations', 'gentiles', 'baptize', 'baptism',
  ],
  repentance: ['repent', 'repented', 'repentance', 'turn', 'return', 'convert'],
  prayer: ['pray', 'prayed', 'prayer', 'intercede', 'supplication', 'petition', 'ask god'],
  wisdom: ['wise', 'wisdom', 'understanding', 'knowledge', 'discernment', 'prudent'],
  humility: ['humble', 'humility', 'meek', 'meekness', 'lowly'],
  pride: ['pride', 'proud', 'arrogant', 'arrogance', 'haughty', 'boast'],
  sin: [
    'sin', 'sins', 'sinner', 'sinful', 'iniquity', 'transgress', 'transgression',
    'trespass', 'wickedness', 'evil', 'abomination',
  ],
  righteousness: [
    'righteous', 'righteousness', 'just', 'justice', 'upright', 'holy',
    'holiness', 'blameless',
  ],
  covenant: ['covenant', 'promise', 'oath', 'vow', 'testament', 'agreement'],
  kingdom: ['kingdom', 'king', 'throne', 'reign', 'rule', 'dominion', 'sovereignty'],
  spirit: [
    'spirit', 'ghost', 'holy ghost', 'holy spirit', 'paraclete', 'comforter',
    'spirit of god', 'spirit of the lord',
  ],
  truth: ['truth', 'true', 'faithful', 'trustworthy', 'honest', 'integrity'],
  light: ['light', 'shine', 'shining', 'lamp', 'torch', 'illumine'],
  darkness: ['darkness', 'dark', 'shadow', 'night', 'blind'],
  praise: ['praise', 'glorify', 'glory', 'exalt', 'magnify', 'bless the lord', 'hallelujah'],
  worship: ['worship', 'worshipped', 'bow', 'kneel', 'prostrate'],
  sacrifice: [
    'sacrifice', 'sacrifices', 'offering', 'burnt offering', 'atonement',
    'altar', 'blood', 'lamb of god',
  ],
  obedience: [
    'obey', 'obedience', 'commandment', 'law', 'statutes', 'precepts', 'keep',
    'disciple', 'disciples', 'follow', 'follower', 'learn', 'teacher',
    'teach', 'training', 'instruction', 'walk', 'imitate', 'example',
    'servant', 'serve',
  ],
  familyAndChildren: [
    'child', 'children', 'son', 'sons', 'daughter', 'father', 'mother',
    'brethren', 'brother', 'sister', 'family', 'husband', 'wife', 'marriage',
  ],
  // Extended coverage for additional valid ConceptId values
  restoration: [
    'restore', 'restoration', 'heal', 'healed', 'healing', 'cure', 'cured',
    'wholeness', 'health', 'sick', 'sickness', 'disease', 'leper', 'leprosy',
    'blind', 'deaf', 'lame', 'miracle', 'mend', 'renewed',
  ],
  shepherd: [
    'shepherd', 'flock', 'pastor', 'elder', 'overseer', 'guide',
    'feed', 'tend', 'watch over', 'strengthen the flock',
  ],
  suffering: [
    'grieve', 'grief', 'mourn', 'mourning', 'weep', 'weeping', 'tears',
    'lament', 'broken heart', 'crushed', 'affliction', 'suffer',
    'suffering', 'pain', 'sorrow', 'distress', 'trouble', 'oppressed',
    'cast down', 'despair', 'anguish',
  ],
  resurrection: [
    'resurrection', 'risen', 'rose from', 'raised', 'eternal life', 'life everlasting',
    'rise again', 'ascension', 'ascended',
  ],
  creation: [
    'creation', 'created', 'creator', 'heaven and earth', 'beginning',
    'made all things', 'works of his hands',
    // Angels fold here (no separate ConceptId).
    'angel', 'angels', 'archangel', 'cherub', 'seraph',
    'host of heaven', 'heavenly host',
  ],
  water: [
    'water', 'river', 'sea', 'ocean', 'rain', 'flood', 'well', 'spring',
    'living water', 'baptize', 'wash', 'clean',
  ],
  bread: [
    'bread', 'loaves', 'manna', 'eat', 'hunger', 'feast', 'supper', 'last supper',
    'communion', 'body of christ', 'broken bread',
  ],
  compassion: [
    'compassion', 'tender', 'kindness', 'goodness', 'care', 'comfort', 'moved',
    'moved with compassion', 'pity', 'helping', 'love for neighbor',
  ],
  death: [
    'death', 'die', 'died', 'dead', 'grave', 'tomb', 'buried', 'mortality',
    'perish', 'perished', 'eternal punishment',
  ],
  exile: [
    'exile', 'captivity', 'captive', 'carried away', 'dispersed', 'scattered',
    'far country', 'prodigal', 'wandering',
  ],
  harvest: [
    'harvest', 'reap', 'reapers', 'sow', 'sown', 'field', 'grain',
    'wheat', 'chaff', 'fruitful', 'fruit',
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Atlas-aware query classification
// ─────────────────────────────────────────────────────────────────────────────

/** Signals that a query is better answered by Atlas than by verse search. */
export type QueryKind = 'scripture' | 'atlas' | 'module';

export type QueryMeta = {
  kind: QueryKind;
  /**
   * For atlas queries: the search term to use for Atlas.
   * For module queries: the module name ('trauma' | 'healing' | 'evangelism' |
   * 'discipleship' | 'pastoral').
   */
  detail?: string;
};

// People, places, and events whose primary home is Atlas.
const ATLAS_PEOPLE = new Set([
  'abraham', 'isaac', 'jacob', 'joseph', 'moses', 'aaron', 'joshua', 'caleb',
  'samson', 'gideon', 'ruth', 'naomi', 'hannah', 'samuel', 'saul', 'david',
  'solomon', 'elijah', 'elisha', 'isaiah', 'jeremiah', 'ezekiel', 'daniel',
  'hosea', 'amos', 'jonah', 'micah', 'zechariah', 'malachi', 'john the baptist',
  'peter', 'paul', 'andrew', 'james', 'john', 'thomas', 'matthew', 'mary',
  'mary magdalene', 'stephen', 'barnabas', 'timothy', 'silas', 'cornelius',
  'adam', 'eve', 'noah', 'lot', 'melchizedek', 'boaz', 'nehemiah', 'ezra',
  'esther', 'mordecai', 'job', 'herod', 'pilate', 'nicodemus', 'lazarus',
  'zacchaeus', 'bartimaeus', 'goliath', 'philistines',
]);

const ATLAS_PLACES = new Set([
  'jerusalem', 'bethlehem', 'nazareth', 'galilee', 'capernaum', 'jericho',
  'bethany', 'samaria', 'judea', 'egypt', 'sinai', 'canaan', 'israel',
  'jordan', 'jordan river', 'dead sea', 'sea of galilee', 'mediterranean',
  'red sea', 'nile', 'euphrates', 'tigris', 'babylon', 'nineveh', 'assyria',
  'persia', 'rome', 'athens', 'corinth', 'ephesus', 'antioch', 'damascus',
  'tyre', 'sidon', 'philippi', 'thessalonica', 'mount sinai', 'mount zion',
  'mount carmel', 'mount of olives', 'garden of gethsemane', 'golgotha',
  'calvary', 'eden', 'garden of eden', 'promised land', 'holy land',
]);

const ATLAS_EVENTS = new Set([
  'exodus', 'exodus from egypt', 'crossing the red sea', 'wilderness',
  'ten commandments', 'tabernacle', 'temple', 'dedication of the temple',
  'exile', 'babylonian exile', 'return from exile', 'pentecost',
  'crucifixion', 'resurrection', 'ascension', 'great flood', 'tower of babel',
  'conquest of canaan', 'divided kingdom', 'fall of jerusalem',
  'birth of jesus', 'baptism of jesus', 'transfiguration', 'palm sunday',
  'last supper', 'triumphal entry',
]);

const ATLAS_EMPIRES = new Set([
  'roman empire', 'roman', 'babylon', 'babylonian', 'assyrian', 'assyria',
  'persian empire', 'persia', 'greek empire', 'greek', 'hellenistic',
  'egyptian empire', 'ottoman', 'seleucid', 'ptolemaic',
]);

const MODULE_SIGNALS: Record<string, string[]> = {
  trauma: [
    'trauma', 'grief', 'loss', 'abuse', 'violence', 'ptsd', 'wound', 'wounded',
    'hurt', 'broken', 'healing from', 'recovering from', 'overcome pain',
  ],
  healing: [
    'healing', 'physical healing', 'emotional healing', 'health', 'restore health',
    'be made whole', 'divine healing', 'miraculous',
  ],
  evangelism: [
    'share my faith', 'tell others', 'witness', 'spread the gospel',
    'win souls', 'outreach', 'missions', 'evangelism',
  ],
  discipleship: [
    'discipleship', 'grow in faith', 'spiritual growth', 'mentor',
    'accountability', 'small group', 'walk with god',
  ],
  pastoral: [
    'pastoral', 'ministry', 'lead a church', 'church leadership',
    'pastor', 'shepherd people', 'care for congregation',
  ],
};

export function classifyQuery(query: string): QueryMeta {
  const lower = query.toLowerCase().trim();

  // Check modules first — most specific.
  for (const [module, signals] of Object.entries(MODULE_SIGNALS)) {
    if (signals.some((s) => lower.includes(s))) {
      return { kind: 'module', detail: module };
    }
  }

  // Check Atlas people.
  for (const name of ATLAS_PEOPLE) {
    if (lower.includes(name)) return { kind: 'atlas', detail: name };
  }

  // Check Atlas places.
  for (const place of ATLAS_PLACES) {
    if (lower.includes(place)) return { kind: 'atlas', detail: place };
  }

  // Check Atlas events.
  for (const event of ATLAS_EVENTS) {
    if (lower.includes(event)) return { kind: 'atlas', detail: event };
  }

  // Check empires.
  for (const empire of ATLAS_EMPIRES) {
    if (lower.includes(empire)) return { kind: 'atlas', detail: empire };
  }

  // "Where is …" / "Who was …" patterns strongly suggest Atlas.
  if (/^(where is|where was|where did|who was|who is|what is|tell me about)\b/.test(lower)) {
    const subject = lower.replace(/^(where is|where was|where did|who was|who is|what is|tell me about)\s+/, '').trim();
    if (subject.length > 1) return { kind: 'atlas', detail: subject };
  }

  return { kind: 'scripture' };
}

// ─────────────────────────────────────────────────────────────────────────────
// Search index
// ─────────────────────────────────────────────────────────────────────────────

export type SearchHit = {
  ref: string;
  bookName: string;
  chapter: number;
  verseNumber: number;
  text: string;
  score: number;
  matched: string[];
  concepts: ConceptId[];
};

type Doc = {
  ref: string;
  bookName: string;
  chapter: number;
  verseNumber: number;
  text: string;
  tokens: Set<string>;
  concepts: ConceptId[];
};

let DOCS: Doc[] | null = null;

function buildDoc(
  bookName: string,
  chapter: number,
  v: { ref: string; number: number; text: string },
): Doc {
  const tokens = new Set(tokenize(v.text));
  const concepts: ConceptId[] = [];
  for (const concept of CONCEPT_REGISTRY) {
    const keywords = CONCEPT_KEYWORDS[concept.id];
    if (!keywords) continue;
    const lower = v.text.toLowerCase();
    if (keywords.some((k) => lower.includes(k))) concepts.push(concept.id);
  }
  return {
    ref: v.ref,
    bookName,
    chapter,
    verseNumber: v.number,
    text: v.text,
    tokens,
    concepts,
  };
}

function buildIndex(chapters: ChapterLike[]): Doc[] {
  const docs: Doc[] = [];
  for (const c of chapters) {
    for (const v of c.verses) {
      docs.push(buildDoc(c.bookName, c.chapter, v));
    }
  }
  return docs;
}

/**
 * Seed the index with an initial batch of chapters (e.g. SAMPLE_CHAPTERS or
 * whatever chapter the reader has loaded). Idempotent — only builds once.
 */
export function ensureIndex(chapters: ChapterLike[]): void {
  if (!DOCS) DOCS = buildIndex(chapters);
}

/**
 * Append more chapters to an existing index without rebuilding.
 * Call this as publisher chapters are loaded so search improves
 * incrementally across a session.
 */
export function appendChapters(chapters: ChapterLike[]): void {
  const existing = DOCS ?? (DOCS = []);
  const existingRefs = new Set(existing.map((d) => d.ref));
  for (const c of chapters) {
    for (const v of c.verses) {
      if (!existingRefs.has(v.ref)) {
        existing.push(buildDoc(c.bookName, c.chapter, v));
        existingRefs.add(v.ref);
      }
    }
  }
}

/** Clear the index — call when the bible/translation changes. */
export function resetIndex(): void {
  DOCS = null;
}

export function searchVerses(query: string, maxHits = 12): SearchHit[] {
  if (!DOCS || DOCS.length === 0) return [];
  const qTokens = tokenize(query);
  if (qTokens.length === 0) return [];

  const queryConcepts = inferConcepts(query.toLowerCase());
  const lowerQuery = query.toLowerCase();

  const hits: SearchHit[] = [];
  for (const d of DOCS) {
    const matched: string[] = [];
    let score = 0;

    // Token match.
    for (const t of qTokens) {
      if (d.tokens.has(t)) {
        matched.push(t);
        score += 2;
      }
    }

    // Exact phrase bonus.
    if (lowerQuery.length > 4 && d.text.toLowerCase().includes(lowerQuery)) {
      score += 6;
    }

    // Concept match.
    for (const c of queryConcepts) {
      if (d.concepts.includes(c)) score += 3;
    }

    if (score > 0) {
      hits.push({
        ref: d.ref,
        bookName: d.bookName,
        chapter: d.chapter,
        verseNumber: d.verseNumber,
        text: d.text,
        score,
        matched,
        concepts: d.concepts,
      });
    }
  }

  hits.sort((a, b) => b.score - a.score);
  return hits.slice(0, maxHits);
}

function inferConcepts(lower: string): ConceptId[] {
  const hits: ConceptId[] = [];
  for (const concept of CONCEPT_REGISTRY) {
    const keywords = CONCEPT_KEYWORDS[concept.id];
    if (!keywords) continue;
    if (keywords.some((k) => lower.includes(k))) hits.push(concept.id);
  }
  return hits;
}

// Keep SampleChapter alias for existing callers.
export type { SampleChapter, SampleVerse };
